/**
 * CleaningProtocolBlock — Éditeur du protocole Cockpit ménage.
 *
 * Blocs opérationnels avec actions à cocher, chronométrage et progression.
 * Distinct de la Liste maîtresse "Préparation jour J" (document imprimable).
 *
 * Garantie de non-perte lors des réorganisations :
 * • Les IDs (bloc.key, item.key) sont STABLES et servent de clé aux :
 *     - reservation.cleaning_prep[item_key] (case cochée)
 *     - cleaning_sessions[bloc_key] (heures/durées/records)
 * • Renommer un titre n'affecte pas la data existante.
 * • Réorganiser (↑↓) n'affecte pas les clés.
 * • Ajouter une action crée une nouvelle clé `custom_xxx`.
 * • Supprimer une action laisse sa clé dans la data existante (invisible).
 */

import { useEffect, useState } from "react";
import { rgApi } from "@/lib/rgApi";
import { DEFAULT_CLEANING_PROTOCOL, mergeProtocolWithDefaults } from "./rgCleaningProtocol";
import "./rg-theme.css";
import "./prep-master-list.css";

function uid() {
  return `custom_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function move(arr, from, to) {
  if (to < 0 || to >= arr.length) return arr;
  const copy = [...arr];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
}

export default function CleaningProtocolBlock() {
  const [blocs, setBlocs] = useState([]);
  const [usingDefaults, setUsingDefaults] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [openIds, setOpenIds] = useState({});
  const [dirty, setDirty] = useState(false);
  const [moveModal, setMoveModal] = useState(null); // { secIdx, iIdx } (item à déplacer vers un autre bloc)

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const d = await rgApi.getCleaningProtocol();
      const source = (d?.using_defaults || !d?.blocs?.length)
        ? DEFAULT_CLEANING_PROTOCOL
        : mergeProtocolWithDefaults(d);
      setBlocs(source.blocs || []);
      setUsingDefaults(!!d?.using_defaults);
      const initOpen = {};
      (source.blocs || []).forEach((b) => { initOpen[b.key] = false; });
      setOpenIds(initOpen);
      setDirty(false);
    } catch (e) {
      setError(String(e.message || e));
      setBlocs(DEFAULT_CLEANING_PROTOCOL.blocs);
    } finally {
      setLoading(false);
    }
  }

  function markDirty() { setDirty(true); setSaved(false); }
  function toggleBloc(id) { setOpenIds((o) => ({ ...o, [id]: !o[id] })); }

  function moveBloc(idx, dir) { setBlocs((prev) => move(prev, idx, idx + dir)); markDirty(); }
  function renameBloc(idx, title) {
    setBlocs((prev) => prev.map((b, i) => (i === idx ? { ...b, title } : b)));
    markDirty();
  }
  function updateBlocSubtitle(idx, subtitle) {
    setBlocs((prev) => prev.map((b, i) => (i === idx ? { ...b, subtitle } : b)));
    markDirty();
  }
  function deleteBloc(idx) {
    const b = blocs[idx];
    if (!confirm(`Supprimer le bloc « ${b.title} » et ses ${b.items.length} actions ?\n\nLes chronométrages (durée cible, dernière, record) associés à ce bloc seront masqués mais conservés en base pour un usage futur.`)) return;
    setBlocs((prev) => prev.filter((_, i) => i !== idx));
    markDirty();
  }
  function addBloc() {
    const b = {
      key: uid().replace("custom_", "custom_bloc_"),
      title: "Nouveau bloc",
      subtitle: "",
      items: [{ key: uid(), label: "Nouvelle action", hidden: false }],
    };
    setBlocs((prev) => [...prev, b]);
    setOpenIds((o) => ({ ...o, [b.key]: true }));
    markDirty();
  }

  function moveItem(secIdx, iIdx, dir) {
    setBlocs((prev) => prev.map((b, i) => {
      if (i !== secIdx) return b;
      return { ...b, items: move(b.items, iIdx, iIdx + dir) };
    }));
    markDirty();
  }
  function renameItem(secIdx, iIdx, label) {
    setBlocs((prev) => prev.map((b, i) => {
      if (i !== secIdx) return b;
      return { ...b, items: b.items.map((it, j) => (j === iIdx ? { ...it, label } : it)) };
    }));
    markDirty();
  }
  function toggleItemHidden(secIdx, iIdx) {
    setBlocs((prev) => prev.map((b, i) => {
      if (i !== secIdx) return b;
      return { ...b, items: b.items.map((it, j) => (j === iIdx ? { ...it, hidden: !it.hidden } : it)) };
    }));
    markDirty();
  }
  function deleteItem(secIdx, iIdx) {
    const item = blocs[secIdx].items[iIdx];
    if (!confirm(`Supprimer l'action « ${item.label} » ?\n\nSi cette action était déjà cochée pour une session, la donnée reste en base mais l'action ne s'affichera plus.`)) return;
    setBlocs((prev) => prev.map((b, i) => {
      if (i !== secIdx) return b;
      return { ...b, items: b.items.filter((_, j) => j !== iIdx) };
    }));
    markDirty();
  }
  function addItem(secIdx) {
    setBlocs((prev) => prev.map((b, i) => {
      if (i !== secIdx) return b;
      return { ...b, items: [...b.items, { key: uid(), label: "Nouvelle action", hidden: false }] };
    }));
    markDirty();
  }
  function moveItemToBloc(fromSecIdx, iIdx, toSecIdx) {
    if (fromSecIdx === toSecIdx) return;
    const item = blocs[fromSecIdx].items[iIdx];
    setBlocs((prev) => prev.map((b, i) => {
      if (i === fromSecIdx) return { ...b, items: b.items.filter((_, j) => j !== iIdx) };
      if (i === toSecIdx) return { ...b, items: [...b.items, item] };
      return b;
    }));
    markDirty();
    setMoveModal(null);
  }

  async function save() {
    setSaving(true); setError("");
    try {
      const d = await rgApi.putCleaningProtocol({ blocs });
      setBlocs(d.blocs || []);
      setUsingDefaults(false);
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(String(e.message || e));
    } finally { setSaving(false); }
  }

  async function resetDefaults() {
    if (!confirm("Restaurer le protocole par défaut ?\n\nVos personnalisations actuelles seront perdues (les données de sessions et chronométrages restent conservés).")) return;
    setSaving(true);
    try {
      await rgApi.resetCleaningProtocol();
      await load();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(String(e.message || e));
    } finally { setSaving(false); }
  }

  if (loading) return <div className="rg-card"><h3 className="rg-h3">🧹 Configuration du Cockpit ménage</h3><p className="rg-muted">Chargement…</p></div>;

  return (
    <div className="rg-card prep-master-block" data-testid="rg-cleaning-protocol-block">
      <h3 className="rg-h3">🧹 Configuration du Cockpit ménage</h3>
      <p className="rg-muted" style={{ margin: "4px 0 8px", fontSize: 12 }}>
        Blocs opérationnels et actions à cocher utilisés pendant une session réelle.
        Distinct de la Liste imprimable « Préparation jour J ».
        {usingDefaults && <> <strong>·</strong> Aucune personnalisation — utilise les défauts.</>}
      </p>
      <div className="rg-alert-warn" style={{ marginBottom: 10, padding: "8px 12px" }}>
        <div className="rg-alert-title" style={{ fontSize: 13 }}>ℹ️ Comportement lors d'une session déjà commencée</div>
        <p className="rg-muted" style={{ margin: "4px 0 0", fontSize: 12, color: "#78350f" }}>
          Les identifiants techniques des blocs et actions sont <strong>stables</strong>.
          Toute réorganisation, renommage ou masquage prend effet dès la <strong>prochaine ouverture</strong> du Cockpit ménage,
          sans réinitialiser les cases cochées, les horaires, les durées cibles ni les records.
          Les actions supprimées deviennent invisibles mais leur historique reste en base.
        </p>
      </div>

      {error && <div className="rg-alert-error">{error}</div>}
      {saved && <div className="rg-alert-success" data-testid="rg-clean-proto-saved">✅ Protocole enregistré</div>}

      <div className="prep-actions">
        <button
          type="button"
          className="rg-btn primary"
          onClick={save}
          disabled={saving || !dirty}
          data-testid="rg-clean-proto-save"
        >
          💾 {saving ? "Enregistrement…" : dirty ? "Enregistrer le protocole" : "Enregistré"}
        </button>
        <button type="button" className="rg-btn ghost danger" onClick={resetDefaults} disabled={saving} data-testid="rg-clean-proto-reset">
          ↺ Restaurer le protocole par défaut
        </button>
      </div>

      <div className="prep-sections">
        {blocs.map((bloc, secIdx) => (
          <div className="prep-section" key={bloc.key} data-testid={`rg-clean-proto-bloc-${bloc.key}`}>
            <div className="prep-section-header">
              <button type="button" className="prep-caret" onClick={() => toggleBloc(bloc.key)}>
                {openIds[bloc.key] ? "▾" : "▸"}
              </button>
              <span style={{ fontSize: 11, color: "#7A5F3E", marginRight: 6, minWidth: 22, textAlign: "right" }}>#{secIdx + 1}</span>
              <input
                className="prep-section-title-input"
                value={bloc.title}
                onChange={(e) => renameBloc(secIdx, e.target.value)}
                data-testid={`rg-clean-proto-bloc-title-${bloc.key}`}
              />
              <div className="prep-section-actions">
                <button type="button" onClick={() => moveBloc(secIdx, -1)} disabled={secIdx === 0} title="Monter">↑</button>
                <button type="button" onClick={() => moveBloc(secIdx, 1)} disabled={secIdx === blocs.length - 1} title="Descendre">↓</button>
                <button type="button" onClick={() => deleteBloc(secIdx)} className="danger">🗑</button>
              </div>
            </div>
            {openIds[bloc.key] && (
              <div className="prep-section-body">
                <label style={{ fontSize: 11, color: "#7A5F3E", display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                  Sous-titre :
                  <input
                    className="prep-item-input"
                    value={bloc.subtitle || ""}
                    onChange={(e) => updateBlocSubtitle(secIdx, e.target.value)}
                    placeholder="ex : ≈ 30 min"
                    style={{ flex: 1 }}
                  />
                </label>
                <div style={{ fontSize: 11, color: "#7A5F3E", marginBottom: 6 }}>
                  ID technique : <code>{bloc.key}</code> · {bloc.items.filter((i) => !i.hidden).length} action{bloc.items.filter((i) => !i.hidden).length > 1 ? "s" : ""} visible{bloc.items.filter((i) => !i.hidden).length > 1 ? "s" : ""} · {bloc.items.length} au total
                </div>
                <ul className="prep-items">
                  {(bloc.items || []).map((item, iIdx) => (
                    <li key={item.key} className="prep-item" style={{ opacity: item.hidden ? 0.5 : 1 }}>
                      <input
                        type="checkbox"
                        checked={!item.hidden}
                        onChange={() => toggleItemHidden(secIdx, iIdx)}
                        title={item.hidden ? "Réafficher" : "Masquer"}
                        data-testid={`rg-clean-proto-item-visible-${item.key}`}
                      />
                      <input
                        className="prep-item-input"
                        value={item.label}
                        onChange={(e) => renameItem(secIdx, iIdx, e.target.value)}
                        style={item.hidden ? { textDecoration: "line-through" } : {}}
                        data-testid={`rg-clean-proto-item-label-${item.key}`}
                      />
                      <div className="prep-item-actions">
                        <button type="button" onClick={() => moveItem(secIdx, iIdx, -1)} disabled={iIdx === 0}>↑</button>
                        <button type="button" onClick={() => moveItem(secIdx, iIdx, 1)} disabled={iIdx === bloc.items.length - 1}>↓</button>
                        <button type="button" onClick={() => setMoveModal({ secIdx, iIdx })} title="Déplacer vers un autre bloc">⇄</button>
                        <button type="button" onClick={() => deleteItem(secIdx, iIdx)} className="danger">🗑</button>
                      </div>
                    </li>
                  ))}
                </ul>
                <button type="button" className="rg-btn tiny ghost" onClick={() => addItem(secIdx)} data-testid={`rg-clean-proto-item-add-${bloc.key}`}>
                  + Ajouter une action
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="prep-footer">
        <button type="button" className="rg-btn ghost" onClick={addBloc} data-testid="rg-clean-proto-bloc-add">
          + Ajouter un bloc
        </button>
      </div>

      {moveModal && (
        <MoveItemModal
          blocs={blocs}
          fromSecIdx={moveModal.secIdx}
          iIdx={moveModal.iIdx}
          onSelect={(toSecIdx) => moveItemToBloc(moveModal.secIdx, moveModal.iIdx, toSecIdx)}
          onCancel={() => setMoveModal(null)}
        />
      )}
    </div>
  );
}

function MoveItemModal({ blocs, fromSecIdx, iIdx, onSelect, onCancel }) {
  const item = blocs[fromSecIdx].items[iIdx];
  return (
    <>
      <div className="rg-modal-back" onClick={onCancel} />
      <div className="rg-modal rg-modal-nested" role="dialog" aria-modal="true" style={{ maxWidth: 480 }}>
        <div className="rg-modal-head">
          <div>
            <div className="rg-tiny">Déplacer une action</div>
            <div className="rg-modal-title">« {item.label} »</div>
          </div>
        </div>
        <div className="rg-modal-body">
          <p className="rg-muted" style={{ fontSize: 12, marginBottom: 10 }}>
            Choisir le bloc de destination. L'ID technique (<code>{item.key}</code>) reste stable :
            la case cochée par une session en cours sera préservée.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {blocs.map((b, i) => (
              <button
                key={b.key}
                type="button"
                className={`rg-btn ${i === fromSecIdx ? "ghost" : "primary"}`}
                onClick={() => onSelect(i)}
                disabled={i === fromSecIdx}
                data-testid={`rg-clean-proto-move-target-${b.key}`}
              >
                {i === fromSecIdx ? "· Bloc actuel : " : "→ "}{b.title}
              </button>
            ))}
          </div>
          <button type="button" className="rg-btn ghost" onClick={onCancel} style={{ marginTop: 10, width: "100%" }}>
            Annuler
          </button>
        </div>
      </div>
    </>
  );
}
